// src/features/statistics/hooks/useDemographicsFilter.ts

import { useMemo, useEffect, useRef, useState } from 'react';

import { accountInsightsAPI } from '../../../shared/api/account-insights.api';
import { applyDemographicsFilter, computeMultiplier } from '../lib/demographics-multiplier';
import { isFilterEmpty } from '../types/demographics-filter';

import type { AccountDemographics } from '../lib/demographics-multiplier';
import type { DemographicsFilter } from '../types/demographics-filter';
import type { AccountStatsRow, DateRangeOption } from '../types/account-stats';
import type { AccountInsightsOptions, InsightBreakdownResult } from '../../../shared/api/account-insights.api';

type AudienceTimeframe = NonNullable<AccountInsightsOptions['timeframe']>;

/**
 * Маппинг диапазона дат на поддерживаемый timeframe Instagram API для
 * reached_audience_demographics. Поддерживаемые значения:
 *   last_14_days, last_90_days, this_month, prev_month
 * (last_30_days не поддерживается — используем last_90_days как ближайший).
 */
export function toTimeframe(dateRange: DateRangeOption): AudienceTimeframe {
  if (dateRange.type === 'preset') {
    if (dateRange.id === 'last7') return 'last_14_days';
    // last_30_days не поддерживается → ближайший доступный last_90_days
    return 'last_90_days';
  }
  if (dateRange.type === 'month') {
    const now = new Date();
    const isCurrentMonth =
      dateRange.year === now.getFullYear() && dateRange.month === now.getMonth() + 1;
    return isCurrentMonth ? 'this_month' : 'prev_month';
  }
  return 'last_90_days';
}

/**
 * Проверяет, точно ли выбранный период в календаре совпадает с поддерживаемыми Instagram API
 * периодами для демографии (чтобы не было путаницы с подменой 30 дней на 90).
 */
export function isExactDemographicsMatch(dateRange: DateRangeOption): boolean {
  if (dateRange.type === 'preset') {
    return dateRange.id === 'last90';
  }
  if (dateRange.type === 'month') {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    if (dateRange.year === currentYear && dateRange.month === currentMonth) return true;
    
    let prevMonth = currentMonth - 1;
    let prevYear = currentYear;
    if (prevMonth === 0) {
      prevMonth = 12;
      prevYear -= 1;
    }
    return dateRange.year === prevYear && dateRange.month === prevMonth;
  }
  return false;
}

function extractBreakdownResults(
  data: Awaited<ReturnType<typeof accountInsightsAPI.getAccountInsights>>,
  label?: string,
  accountId?: string,
): InsightBreakdownResult[] {
  const breakdown = data.data[0]?.total_value?.breakdowns?.[0];
  const results = breakdown?.results ?? [];
  if (label && results.length === 0 && breakdown) {
    console.warn(`[demographics] ${label} for ${accountId}: breakdown exists but results empty. Keys: ${JSON.stringify(breakdown.dimension_keys)}, full breakdown:`, JSON.stringify(breakdown));
  }
  return results;
}

async function fetchAccountDemographics(
  accountId: string,
  timeframe: AudienceTimeframe,
): Promise<AccountDemographics> {
  const paramsReach = {
    metric: 'reached_audience_demographics',
    period: 'lifetime' as const,
    metricType: 'total_value' as const,
    timeframe,
  };
  const paramsFollowers = {
    metric: 'follower_demographics',
    period: 'lifetime' as const,
    metricType: 'total_value' as const,
    timeframe,
  };

  const [
    rGender, rAgeGender, rCountry, rCity,
    fGender, fAgeGender, fCountry, fCity
  ] = await Promise.all([
    accountInsightsAPI.getAccountInsights(accountId, { ...paramsReach, breakdown: 'gender' }),
    accountInsightsAPI.getAccountInsights(accountId, { ...paramsReach, breakdown: 'age,gender' }),
    accountInsightsAPI.getAccountInsights(accountId, { ...paramsReach, breakdown: 'country' }),
    accountInsightsAPI.getAccountInsights(accountId, { ...paramsReach, breakdown: 'city' }),
    accountInsightsAPI.getAccountInsights(accountId, { ...paramsFollowers, breakdown: 'gender' }),
    accountInsightsAPI.getAccountInsights(accountId, { ...paramsFollowers, breakdown: 'age,gender' }),
    accountInsightsAPI.getAccountInsights(accountId, { ...paramsFollowers, breakdown: 'country' }),
    accountInsightsAPI.getAccountInsights(accountId, { ...paramsFollowers, breakdown: 'city' }),
  ]);

  return {
    reach: {
      genderResults: extractBreakdownResults(rGender, 'reach_gender', accountId),
      ageGenderResults: extractBreakdownResults(rAgeGender, 'reach_age,gender', accountId),
      countryResults: extractBreakdownResults(rCountry, 'reach_country', accountId),
      cityResults: extractBreakdownResults(rCity, 'reach_city', accountId),
    },
    followers: {
      genderResults: extractBreakdownResults(fGender, 'followers_gender', accountId),
      ageGenderResults: extractBreakdownResults(fAgeGender, 'followers_age,gender', accountId),
      countryResults: extractBreakdownResults(fCountry, 'followers_country', accountId),
      cityResults: extractBreakdownResults(fCity, 'followers_city', accountId),
    }
  };
}

type UseDemographicsFilterResult = {
  filteredRows: AccountStatsRow[];
  unavailableIds: Set<string>;
  isFetchingDemographics: boolean;
  availableCountries: string[];
  availableCities: string[];
};

export function useDemographicsFilter(
  rows: AccountStatsRow[],
  activeFilter: DemographicsFilter,
  dateRange: DateRangeOption,
  shouldFetch: boolean,
): UseDemographicsFilterResult {
  const [cache, setCache] = useState<Map<string, AccountDemographics>>(new Map());
  const [unavailableIds, setUnavailableIds] = useState<Set<string>>(new Set());
  const [isFetchingDemographics, setIsFetchingDemographics] = useState(false);
  const fetchedTimeframeRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const timeframe = toTimeframe(dateRange);

  const accountIdsKey = useMemo(
    () => rows.map(r => r.accountId).sort().join(','),
    [rows]
  );

  useEffect(() => {
    if (!shouldFetch) return;
    if (rows.length === 0) return;

    // Reset cache if timeframe changed
    if (fetchedTimeframeRef.current !== null && fetchedTimeframeRef.current !== timeframe) {
      setCache(new Map());
      setUnavailableIds(new Set());
      fetchedTimeframeRef.current = null;
    }

    if (fetchedTimeframeRef.current === timeframe) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsFetchingDemographics(true);

    const accountIds = rows.map(r => r.accountId);

    Promise.allSettled(
      accountIds.map(id =>
        fetchAccountDemographics(id, timeframe).then(demo => ({ id, demo }))
      )
    ).then(results => {
      if (controller.signal.aborted) return;

      const newCache = new Map<string, AccountDemographics>();
      const newUnavailable = new Set<string>();

      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          newCache.set(result.value.id, result.value.demo);
        } else {
          console.error('[demographics] fetch failed for', accountIds[i], result.reason);
          newUnavailable.add(accountIds[i]);
        }
      });

      console.log('[demographics] cache populated:', newCache.size, 'accounts, unavailable:', newUnavailable.size);
      if (newCache.size > 0) {
        const first = newCache.values().next().value;
        console.log('[demographics] sample genderResults:', first?.genderResults);
      }

      fetchedTimeframeRef.current = timeframe;
      setCache(newCache);
      setUnavailableIds(newUnavailable);
      setIsFetchingDemographics(false);
    });

    // No cleanup return — fetch must complete even if panel closes (Применить aborts shouldFetch)
  }, [shouldFetch, timeframe, accountIdsKey]);

  // Abort only on unmount, not on every re-render
  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  const filteredRows = useMemo(() => {
    if (!isExactDemographicsMatch(dateRange)) {
      return rows; // Do not apply filters if the date range doesn't perfectly match
    }
    
    if (isFilterEmpty(activeFilter)) return rows;

    return rows.map(row => {
      const demo = cache.get(row.accountId);
      if (!demo) return row; // Нет демографии -> множитель 1
      return applyDemographicsFilter(row, computeMultiplier(demo, activeFilter));
    });
  }, [rows, cache, activeFilter, dateRange]);

  const availableCountries = useMemo(() => {
    const set = new Set<string>();
    for (const demo of cache.values()) {
      for (const r of demo.reach.countryResults) set.add(r.dimension_values[0]);
    }
    return Array.from(set).sort();
  }, [cache]);

  const availableCities = useMemo(() => {
    const set = new Set<string>();
    for (const demo of cache.values()) {
      for (const r of demo.reach.cityResults) set.add(r.dimension_values[0]);
    }
    return Array.from(set).sort();
  }, [cache]);

  return { filteredRows, unavailableIds, isFetchingDemographics, availableCountries, availableCities };
}
